import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { APP_SCHEMA_ENDPOINTS } from '../api';
import { Observable, catchError, map, of, tap } from 'rxjs';
import type { ContactPhotoExistsResponse, PhotoFormat } from '../models/contact-photo.model';

/**
 * Contact Photo Service
 *
 * Handles contact photo operations with caching and error handling.
 * Combines low-level HTTP operations with high-level caching logic.
 * Following Angular best practices with signals and dependency injection.
 */
@Injectable({
  providedIn: 'root',
})
export class ContactPhotoService {
  private http = inject(HttpClient);

  // Photo cache using signals
  private photoUrlCache = signal<Map<string, string>>(new Map());
  private photoExistsCache = signal<Map<string, boolean>>(new Map());
  private photoVersionCache = signal<Map<string, number>>(new Map()); // Track photo versions

  // ========================
  // PRIVATE HTTP METHODS
  // ========================

  /**
   * Get contact photo as blob data (actual image file) - HTTP call
   */
  private getContactPhotoHttp(
    contactId: number,
    format: PhotoFormat = 'jpg',
    bustCache: boolean = false
  ): Observable<Blob> {
    // Use format parameter and version for cache busting
    const cacheKey = `${contactId}-${format}`;
    const version = this.photoVersionCache().get(cacheKey) || 0;

    let params = new HttpParams().set('format', format).set('v', version.toString()); // Always include version for proper caching

    if (bustCache) {
      params = params.set('_t', Date.now().toString());
    }

    return this.http
      .get(`${APP_SCHEMA_ENDPOINTS.contacts.list}${contactId}/photo`, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
        params,
        responseType: 'blob',
      })
      .pipe(
        catchError((error) => {
          // 404 is expected when no photo exists - don't log as error
          if (error.status === 404) {
            return of(new Blob()); // Return empty blob
          }
          console.error('❌ ContactPhotoService HTTP Error:', error);
          throw error;
        })
      );
  }

  /**
   * Check if contact has a photo - HTTP call
   */
  private checkContactPhotoExistsHttp(
    contactId: number,
    format: PhotoFormat = 'jpg'
  ): Observable<ContactPhotoExistsResponse> {
    const params = new HttpParams().set('format', format);

    return this.http
      .get<ContactPhotoExistsResponse>(
        `${APP_SCHEMA_ENDPOINTS.contacts.list}${contactId}/photo/exists`,
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
          }),
          params,
        }
      )
      .pipe(
        catchError((error) => {
          // 404 is expected when no photo exists
          if (error.status === 404) {
            return of({
              contact_id: contactId,
              has_photo: false,
              format: format,
              checked_format: format,
            });
          }
          console.error('❌ ContactPhotoService HTTP Error:', error);
          throw error;
        })
      );
  }

  // ========================
  // PUBLIC CACHED METHODS
  // ========================

  /**
   * Get contact photo URL with caching
   * Uses blob loading to bypass Azure Storage access restrictions
   * Optimized: Instant return from cache, efficient Map operations
   */
  getContactPhotoUrl(
    contactId: number,
    format: PhotoFormat = 'jpg',
    bustCache: boolean = false
  ): Observable<string | null> {
    const cacheKey = `${contactId}-${format}`;
    const cachedUrl = this.photoUrlCache().get(cacheKey);

    // Return cached URL if available - instant return for better UX (unless cache busting is requested)
    if (cachedUrl && !bustCache) {
      return of(cachedUrl);
    }

    // Check if we already know this photo doesn't exist (cached 404) - unless cache busting is requested
    const cachedExists = this.photoExistsCache().get(cacheKey);
    if (cachedExists === false && !bustCache) {
      // Avoid HTTP request - we already know there's no photo
      return of(null);
    }

    // Load photo as blob and create object URL
    return this.getContactPhotoHttp(contactId, format, bustCache).pipe(
      map((blob: Blob) => {
        // Check if blob is empty (no photo)
        if (blob.size === 0) {
          // Cache the "no photo" state to avoid future HTTP calls
          this.photoExistsCache.update((cache) => {
            const newCache = new Map(cache);
            newCache.set(cacheKey, false);
            return newCache;
          });
          return null;
        }

        // Revoke old object URL if it exists to prevent memory leaks and stale data
        const oldUrl = this.photoUrlCache().get(cacheKey);
        if (oldUrl) {
          URL.revokeObjectURL(oldUrl);
        }

        // Create object URL from blob
        const objectUrl = URL.createObjectURL(blob);

        // Batch all cache updates in one operation to minimize signal updates
        this.photoUrlCache.update((urlCache) => {
          const newUrlCache = new Map(urlCache);
          newUrlCache.set(cacheKey, objectUrl);

          // Also update exists cache in same operation
          this.photoExistsCache.update((existsCache) => {
            const newExistsCache = new Map(existsCache);
            newExistsCache.set(cacheKey, true);
            return newExistsCache;
          });

          return newUrlCache;
        });

        return objectUrl;
      }),
      catchError(() => {
        // Silent fail - no photo available, cache this result
        this.photoExistsCache.update((cache) => {
          const newCache = new Map(cache);
          newCache.set(cacheKey, false);
          return newCache;
        });
        return of(null);
      })
    );
  }

  /**
   * Check if contact has a photo with caching
   */
  checkContactPhotoExists(contactId: number, format: PhotoFormat = 'jpg'): Observable<boolean> {
    const cacheKey = `${contactId}-${format}`;
    const cachedExists = this.photoExistsCache().get(cacheKey);

    // Return cached result if available
    if (cachedExists !== undefined) {
      return of(cachedExists);
    }

    return this.checkContactPhotoExistsHttp(contactId, format).pipe(
      map((response: ContactPhotoExistsResponse) => response.has_photo),
      tap((hasPhoto) => {
        // Cache the result
        this.photoExistsCache.update((cache) => {
          const newCache = new Map(cache);
          newCache.set(cacheKey, hasPhoto);
          return newCache;
        });
      }),
      catchError((error) => {
        console.warn(`Failed to check photo existence for contact ${contactId}:`, error);
        return of(false);
      })
    );
  }
  /**
   * Get contact photo as blob (for download or processing)
   */
  getContactPhotoBlob(
    contactId: number,
    format: PhotoFormat = 'jpg',
    bustCache: boolean = false
  ): Observable<Blob | null> {
    return this.getContactPhotoHttp(contactId, format, bustCache).pipe(
      catchError((error) => {
        console.warn(`Failed to load photo blob for contact ${contactId}:`, error);
        return of(null);
      })
    );
  }

  /**
   * Clear photo cache for a specific contact
   */
  clearContactPhotoCache(contactId: number): void {
    this.photoUrlCache.update((cache) => {
      const newCache = new Map(cache);
      // Remove all entries for this contact ID and revoke object URLs
      for (const [key, url] of newCache.entries()) {
        if (key.startsWith(`${contactId}-`)) {
          // Revoke object URL to prevent memory leaks
          URL.revokeObjectURL(url);
          newCache.delete(key);
        }
      }
      return newCache;
    });

    this.photoExistsCache.update((cache) => {
      const newCache = new Map(cache);
      // Remove all entries for this contact ID
      for (const key of newCache.keys()) {
        if (key.startsWith(`${contactId}-`)) {
          newCache.delete(key);
        }
      }
      return newCache;
    });

    // Increment version to bust browser cache
    this.photoVersionCache.update((cache) => {
      const newCache = new Map(cache);
      for (const key of newCache.keys()) {
        if (key.startsWith(`${contactId}-`)) {
          const currentVersion = newCache.get(key) || 0;
          newCache.set(key, currentVersion + 1);
        }
      }
      return newCache;
    });
  }

  /**
   * Clear all photo caches
   */
  clearAllPhotoCache(): void {
    // Revoke all object URLs to prevent memory leaks
    for (const url of this.photoUrlCache().values()) {
      URL.revokeObjectURL(url);
    }

    this.photoUrlCache.set(new Map());
    this.photoExistsCache.set(new Map());
    this.photoVersionCache.set(new Map());
  }

  /**
   * Get cache statistics (computed signal for debugging)
   */
  readonly cacheStats = computed(() => ({
    urlCacheSize: this.photoUrlCache().size,
    existsCacheSize: this.photoExistsCache().size,
    versionCacheSize: this.photoVersionCache().size,
  }));

  // ========================
  // UPLOAD METHODS
  // ========================

  /**
   * Upload a contact photo
   * @param contactId The contact ID
   * @param file The image file to upload
   * @param overwrite Whether to overwrite existing photo (default: true)
   * @returns Observable with upload response
   */
  uploadContactPhoto(
    contactId: number,
    file: File,
    overwrite: boolean = true
  ): Observable<{
    success: boolean;
    contact_id: number;
    filename: string;
    format: string;
    size_bytes: number;
    content_type: string;
    overwritten: boolean;
    uploaded_photo_url?: string; // New: immediate photo URL
  }> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const params = new HttpParams().set('overwrite', overwrite.toString());

    // WICHTIG: Kein Content-Type Header setzen, Angular setzt automatisch multipart/form-data mit boundary
    return this.http
      .post<{
        success: boolean;
        contact_id: number;
        filename: string;
        format: string;
        size_bytes: number;
        content_type: string;
        overwritten: boolean;
      }>(`${APP_SCHEMA_ENDPOINTS.contacts.list}${contactId}/photo`, formData, {
        params,
      })
      .pipe(
        tap((response) => {
          // Clear cache and immediately cache the uploaded file for instant display
          if (response.success) {
            this.clearContactPhotoCache(contactId);

            // Immediately cache the uploaded file as blob URL for instant display
            const format = response.format || 'jpg';
            const cacheKey = `${contactId}-${format}`;
            const objectUrl = URL.createObjectURL(file);

            this.photoUrlCache.update((cache) => {
              const newCache = new Map(cache);
              newCache.set(cacheKey, objectUrl);
              return newCache;
            });

            this.photoExistsCache.update((cache) => {
              const newCache = new Map(cache);
              newCache.set(cacheKey, true);
              return newCache;
            });
          }
        }),
        catchError((error) => {
          console.error('❌ ContactPhotoService Upload Error:', error);
          console.error('Error details:', error.error);
          throw error;
        })
      );
  }

  /**
   * Update a contact photo (alias for upload with overwrite=true)
   * @param contactId The contact ID
   * @param file The image file to upload
   * @returns Observable with upload response
   */
  updateContactPhoto(
    contactId: number,
    file: File
  ): Observable<{
    success: boolean;
    contact_id: number;
    filename: string;
    format: string;
    size_bytes: number;
    content_type: string;
    overwritten: boolean;
  }> {
    return this.uploadContactPhoto(contactId, file, true);
  }

  /**
   * Delete a contact photo
   * @param contactId The contact ID
   * @param format Image format to delete (default: 'jpg')
   * @returns Observable with deletion response
   */
  deleteContactPhoto(
    contactId: number,
    format: PhotoFormat = 'jpg'
  ): Observable<{
    success: boolean;
    contact_id: number;
    format: string;
    message: string;
  }> {
    const params = new HttpParams().set('format', format);

    return this.http
      .delete<{
        success: boolean;
        contact_id: number;
        format: string;
        message: string;
      }>(`${APP_SCHEMA_ENDPOINTS.contacts.list}${contactId}/photo`, { params })
      .pipe(
        tap(() => {
          // Clear cache for this contact after successful deletion
          this.clearContactPhotoCache(contactId);
        }),
        catchError((error) => {
          console.error('❌ ContactPhotoService Delete Error:', error);
          throw error;
        })
      );
  }
}
